// src/screens/Recipes/Recipes.tsx
import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useRecipe } from '../../context/RecipeContext'
import RecipeDetail from '../Recipe/RecipeDetail'
import styles from './Recipes.module.css'
import PageContainer from '../../components/PageElements/PageContainer/PageContainer'
import PageBody from '../../components/PageElements/PageBody/PageBody'
import Header from '../../components/Header'
import ConfirmationDialog from '../../components/ConfirmationDialog'
import RecipeTypeIndicator from '../../components/RecipeTypeIndicator'
import { useNavigate } from 'react-router-dom';
import RecipeCard from '../../components/RecipeCard'
import config from '../../config'
import { usePageSEO } from '../../hooks/usePageSEO'
import RecipeDietaryAttributes from '../../components/RecipeDietaryAttributes/RecipeDietaryAttributes'

// List of blocked domains (none for now)
const BLOCKED_DOMAINS: string[] = []

const isValidHttpUrl = (string: string): boolean => {
  try {
    const url = new URL(string)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const isBlockedDomain = (string: string): boolean => {
  try {
    const url = new URL(string)
    return BLOCKED_DOMAINS.some(domain => url.hostname.includes(domain))
  } catch {
    return false
  }
}

const Recipes: React.FC = () => {
  return <RecipesContent />
}

const RecipesContent: React.FC = () => {
  // SEO hook for the recipes page
  usePageSEO({
    title: "Discover Recipes for Your Diet | PlatefullAI - Personalized Recipe Discovery",
    description: "Discover and save recipes that match your digital dietary identity. Find recipes tailored to your dietary preferences, restrictions, and nutritional needs from any website.",
    keywords: "personalized recipe discovery, dietary preferences, recipe personalization, digital diet identity, food allergies, dietary restrictions, recipe filtering, nutritional needs, food technology, dietary recipes",
    canonicalUrl: "https://platefullai.com/recipes"
  });

  const {
    url,
    setUrl,
    fetchRecipe,
    isLoading,
    recipe,           // the "current" recipe fetched by URL
    savedRecipes,     // your array of full Recipe objects (kept for backward compatibility)
    fetchSaved,
    save,
    unsave,
    allUserRecipes,   // unified recipes (saved + created + variants)
    fetchAllUserRecipes,
    deleteRecipe,
    deleteVariant,
    searchRecipes,
  } = useRecipe()
  const { logout, user, getUserPreferences } = useAuth()
  const navigate = useNavigate();

  const [touched, setTouched] = useState(false)
  const [isValidUrl, setIsValidUrl] = useState(false)
  const [isBlockedUrl, setIsBlockedUrl] = useState(false)
  const [searchResults, setSearchResults] = useState<{ items: typeof allUserRecipes; total_pages: number; page: number; page_size: number; total_count: number } | null>(null)
  const [status, setStatus] = useState<{ text: string; running: boolean }>({ text: '', running: false })
  const [browseRecipes, setBrowseRecipes] = useState<typeof allUserRecipes>([])
  const [browseLoading, setBrowseLoading] = useState(true)
  const [browseError, setBrowseError] = useState<string | null>(null)
  const [browsePage, setBrowsePage] = useState(1)
  const [browsePageSize, setBrowsePageSize] = useState(20)
  const [browseTotalPages, setBrowseTotalPages] = useState(1)
  const [userPreferences, setUserPreferences] = useState<any>(null)

  // NEW: track which saved recipe (if any) is selected
  const [selectedRecipe, setSelectedRecipe] = useState<typeof recipe | null>(null)
  const [showUnsaveDialog, setShowUnsaveDialog] = useState(false)
  const [recipeToUnsave, setRecipeToUnsave] = useState<string | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [recipeToDelete, setRecipeToDelete] = useState<{ id: string; type: string } | null>(null)

  // Anchor to scroll to when a recipe is selected / shown
  const detailAnchorRef = useRef<HTMLDivElement | null>(null)

  // Load user preferences when user logs in
  useEffect(() => {
    if (user) {
      getUserPreferences().then(prefs => {
        if (prefs) {
          setUserPreferences(prefs);
        }
      });
    }
  }, [user, getUserPreferences]);

  // Helper function to get matching dietary tags
  const getMatchingDietaryTags = (dietaryInfo: any) => {
    if (!userPreferences || !dietaryInfo) return [];
    
    const matchingTags = [];
    const dietaryMapping = {
      'vegan': 'Vegan',
      'vegetarian': 'Vegetarian',
      'pescatarian': 'Pescatarian',
      'gluten_free': 'Gluten Free',
      'dairy_free': 'Dairy Free',
      'nut_free': 'Nut Free',
      'egg_free': 'Egg Free',
      'soy_free': 'Soy Free',
      'shellfish_free': 'Shellfish Free',
      'fish_free': 'Fish Free',
      'sesame_free': 'Sesame Free',
      'low_fodmap': 'Low FODMAP',
      'keto': 'Keto',
      'paleo': 'Paleo',
      'atkins': 'Atkins',
      'corn_free': 'Corn Free',
      'mustard_free': 'Mustard Free'
    };
    
    for (const [key, label] of Object.entries(dietaryMapping)) {
      if (userPreferences[key] && dietaryInfo[key]) {
        matchingTags.push({ key, label });
      }
    }
    
    return matchingTags;
  };

  // Load all user recipes (saved + created + variants) once user logs in
  useEffect(() => {
    if (user) {
      fetchAllUserRecipes()
      // Keep fetchSaved for backward compatibility with other components
      fetchSaved()
    }
  }, [user, fetchAllUserRecipes, fetchSaved])

  // Default browse: list paginated recipes when not searching
  const fetchBrowseRecipes = async (page: number, pageSize: number) => {
    try {
      setBrowseLoading(true)
      setBrowseError(null)
      const res = await fetch(`${config.API_BASE_URL}/api/recipes?page=${page}&page_size=${pageSize}`)
      if (!res.ok) throw new Error('Failed to load recipes')
      const data = await res.json()
      setBrowseRecipes(data.items || [])
      setBrowseTotalPages(Math.max(1, data.total_pages || 1))
    } catch (e) {
      setBrowseError(e instanceof Error ? e.message : 'Failed to load recipes')
    } finally {
      setBrowseLoading(false)
    }
  }

  useEffect(() => {
    if (!searchResults) {
      fetchBrowseRecipes(browsePage, browsePageSize)
    }
  }, [browsePage, browsePageSize, searchResults])

  // validate URL
  useEffect(() => {
    const isValid = isValidHttpUrl(url)
    const isBlocked = isBlockedDomain(url)
    setIsValidUrl(isValid && !isBlocked)
    setIsBlockedUrl(isBlocked)
  }, [url])

  const handleLogin = () => {
    // TODO: Navigate to login page or show login modal
    console.log('Login clicked');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isValidUrl) {
      setStatus({ text: 'URL detected — searching database for URL', running: true })
      // Try to find by source_url first
      const existing = await searchRecipes({ source_url: url.trim(), page: 1, page_size: 20 })
      if (existing.total_count > 0) {
        setStatus({ text: `URL detected — found ${existing.total_count} in database`, running: false })
        setSearchResults(existing)
        setSelectedRecipe(null)
        return
      }
      setStatus({ text: 'URL not found in DB — parsing', running: true })
      await fetchRecipe()
      setStatus({ text: 'Extracted ingredients & instructions', running: false })
      setSelectedRecipe(null)
      setSearchResults(null)
    } else if (url && url.trim()) {
      setStatus({ text: 'Search detected — searching database', running: true })
      const data = await searchRecipes({ q: url.trim(), page: 1, page_size: 20 })
      setStatus({ text: `Search detected — found ${data.total_count} results`, running: false })
      setSearchResults(data)
      setSelectedRecipe(null)
    }
  }

  const handleSearchPageChange = async (nextPage: number) => {
    if (!searchResults) return
    const data = await searchRecipes({ q: url.trim(), page: nextPage, page_size: searchResults.page_size })
    setSearchResults(data)
    if (detailAnchorRef.current) {
      const y = detailAnchorRef.current.getBoundingClientRect().top + window.pageYOffset - 72
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  const handleBrowsePageChange = (nextPage: number) => {
    setBrowsePage(nextPage)
    window.scrollTo({ top: 0 })
  }

  const handleBrowsePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBrowsePageSize(parseInt(e.target.value, 10))
    setBrowsePage(1)
  }

  const handleClearSearch = () => {
    setSearchResults(null)
    setUrl('')
    setStatus({ text: '', running: false })
    setSelectedRecipe(null)
  }

  const handleUnsaveClick = (recipeId: string) => {
    setRecipeToUnsave(recipeId)
    setShowUnsaveDialog(true)
  }

  const handleUnsaveConfirm = async () => {
    if (!recipeToUnsave) {
      return
    }
    
    await unsave(recipeToUnsave)
    setShowUnsaveDialog(false)
    setRecipeToUnsave(null)
  }

  const handleUnsaveCancel = () => {
    setShowUnsaveDialog(false)
    setRecipeToUnsave(null)
  }

  const handleDeleteConfirm = async () => {
    if (!recipeToDelete) {
      return
    }
    
    try {
      if (recipeToDelete.type === 'variant') {
        await deleteVariant(recipeToDelete.id)
      } else {
        await deleteRecipe(recipeToDelete.id)
      }
      
      // Refresh the recipes list
      if (user) {
        fetchAllUserRecipes()
      }
    } catch (error) {
      console.error('Failed to delete recipe:', error)
    } finally {
      setShowDeleteDialog(false)
      setRecipeToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false)
    setRecipeToDelete(null)
  }

  const handleCustomizeClick = () => {
    if (detailRecipe && user) {
      if (detailRecipe.type === 'variant') {
        // For variant recipes, navigate to edit the existing variant
        navigate(`/variants/${detailRecipe.id}/edit`, { 
          state: { recipe: detailRecipe } 
        });
      } else {
        // For regular recipes, navigate to customize page to create a new variant
        navigate(`/recipes/${detailRecipe.id}/customize`, { 
          state: { recipe: detailRecipe } 
        });
      }
    }
  }

  // NEW: if user clicked a saved recipe, override detail props
  const detailRecipe = selectedRecipe ?? recipe

  // Smoothly scroll to the detail section when it appears/changes
  useEffect(() => {
    if (detailRecipe && detailAnchorRef.current) {
      const y =
        detailAnchorRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        72 // offset for fixed header
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }, [detailRecipe])

  return (
    <PageContainer>
      <Header user={user} onLogin={handleLogin} onLogout={logout} />

      <PageBody>
        {/* Recipe Navigation Links */}
        <div className={styles.recipeHeader}>
          <div className={styles.recipeHeaderContent}>
            <div className={styles.recipeHeaderLinks}>
              <button 
                className={styles.headerLink}
                onClick={() => navigate('/saved')}
              >
                My Recipes
              </button>
              <button 
                className={styles.headerLink}
                onClick={() => navigate('/grocery-list')}
              >
                Grocery List
              </button>
            </div>
          </div>
        </div>

        {/* URL input + fetch form */}
        <div className={styles.formSection}>
          <div className={styles.formHeader}>
            <h2 className={styles.formTitle}>Discover and Save Recipes</h2>
            <p className={styles.formDescription}>
              Save any recipe without the story, ads, or clutter — or search our recipe database. 
              See how your ID helps you find recipes that match your dietary preferences.
            </p>
          </div>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <input
                type="text"
                value={url}
                onChange={e => {
                  setTouched(true)
                  setUrl(e.target.value)
                }}
                placeholder="Paste a recipe URL to extract, or search by title/ingredient keywords"
                className={styles.input}
              />
              <button
                type="submit"
                disabled={(!isValidUrl && !url) || isLoading}
                className={`${styles.button} ${
                  (isValidUrl || (url && url.trim())) ? styles.buttonEnabled : styles.buttonDisabled
                }`}
              >
                {isLoading ? 'Working...' : isValidUrl ? 'Save Recipe' : 'Search'}
              </button>
              {searchResults && searchResults.items.length > 0 && (
                <button type="button" className={styles.button} onClick={handleClearSearch}>
                  Clear
                </button>
              )}
            </div>
            {status.text && !(searchResults && searchResults.items.length > 0) && (
              <div className={styles.statusBar}>
                <span className={styles.statusDot} />
                <span className={styles.statusText}>{status.text}</span>
                {status.running && (
                  <span className={styles.typingDots} aria-hidden="true">
                    <span className={styles.dot}></span>
                    <span className={styles.dot}></span>
                    <span className={styles.dot}></span>
                  </span>
                )}
              </div>
            )}
            {touched && isBlockedUrl && url && (
              <p className={styles.errorText}>
                Sorry, we can't extract recipes from this website at the moment. Please try a different recipe URL.
              </p>
            )}
          </form>
          
        </div>

        {isLoading && <p>Loading recipe...</p>}

        {/* Search results list */}
        {searchResults && searchResults.items.length > 0 && (
          <div>
            <h2 className={styles.sectionHeading}>Search Results</h2>
            <ul className={styles.savedList}>
              {searchResults.items.map(r => (
                <li key={r.id}>
                  <button
                    className={styles.savedLink}
                    onClick={() => setSelectedRecipe(r)}
                  >
                      <div className={styles.thumb}>
                        {(() => { const p = r.photos?.find(p=>p.is_primary)||r.photos?.[0]; const src = p?.url || (r as any).image; return src ? <img src={src} alt=""/> : null })()}
                      </div>
                    <div className={styles.recipeTitleRow}>
                      <span className={styles.recipeTitle}>{r.title}</span>
                    </div>
                    {r.dietary_info && (
                      <div className={styles.dietaryTagsRow}>
                        <RecipeDietaryAttributes
                          dietaryAttributes={r.dietary_info}
                          userPreferences={userPreferences}
                          variant="compact"
                          showOnlyMatches={true}
                          maxTags={2}
                        />
                      </div>
                    )}
                    {r.source_url && (
                      <span className={styles.recipeSource}>
                        Source: {new URL(r.source_url).hostname}
                      </span>
                    )}
                    <span className={styles.recipeMeta}>
                      {r.type && <RecipeTypeIndicator type={r.type} />}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {searchResults.total_pages > 1 && (
              <div className={styles.bottomPaginationBar}>
                <div className={styles.pageStatus}>Page {searchResults.page} of {searchResults.total_pages}</div>
                <div className={styles.pageActions}>
                  <button
                    className={styles.button}
                    onClick={() => handleSearchPageChange(searchResults.page + 1)}
                    disabled={searchResults.page === searchResults.total_pages}
                  >
                    Next
                  </button>
                  <button
                    className={styles.button}
                    onClick={() => handleSearchPageChange(searchResults.total_pages)}
                    disabled={searchResults.page === searchResults.total_pages}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Default browse grid when not searching */}
        {!searchResults && (
          <>
            <div className={styles.controlsRow}
                 style={{ maxWidth: 1200, margin: '0 auto 12px auto', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <label style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>Results per page:&nbsp;
                <select value={browsePageSize} onChange={handleBrowsePageSizeChange}
                        style={{ background: 'var(--color-bg-card)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)', borderRadius: 6, padding: '4px 6px' }}>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </label>
            </div>
            <div className={styles.recipesGrid}>
              {!browseLoading && !browseError && browseRecipes.map(r => (
                <RecipeCard
                  key={r.id}
                  recipe={r as any}
                  isSaved={false}
                  onViewClick={(rec) => setSelectedRecipe(rec as any)}
                  dietaryInfo={r.dietary_info}
                  userPreferences={userPreferences}
                  matchingDietaryTags={getMatchingDietaryTags(r.dietary_info)}
                />
              ))}
            </div>
            {!browseLoading && browseError && (
              <p style={{ color: 'var(--color-text-error)', textAlign: 'center' }}>{browseError}</p>
            )}
            {browseLoading && (
              <p style={{ color: 'var(--color-text-secondary)', textAlign: 'center' }}>Loading recipes…</p>
            )}
            {browseTotalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageButton}
                  onClick={() => handleBrowsePageChange(browsePage - 1)}
                  disabled={browsePage === 1}
                >Previous</button>
                <div className={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, browseTotalPages) }, (_, i) => {
                    let pageNum: number
                    if (browseTotalPages <= 5) pageNum = i + 1
                    else if (browsePage <= 3) pageNum = i + 1
                    else if (browsePage >= browseTotalPages - 2) pageNum = browseTotalPages - 4 + i
                    else pageNum = browsePage - 2 + i
                    return (
                      <button
                        key={pageNum}
                        className={`${styles.pageButton} ${browsePage === pageNum ? styles.active : ''}`}
                        onClick={() => handleBrowsePageChange(pageNum)}
                      >{pageNum}</button>
                    )
                  })}
                </div>
                <button
                  className={styles.pageButton}
                  onClick={() => handleBrowsePageChange(browsePage + 1)}
                  disabled={browsePage === browseTotalPages}
                >Next</button>
              </div>
            )}
          </>
        )}

        {/* Render detail view if we have one */}
        {detailRecipe && (
          <div ref={detailAnchorRef}>
          <RecipeDetail
            recipe={detailRecipe}
            // coerce savedRecipes into an array before calling .some()
            isSaved={Array.isArray(savedRecipes) && savedRecipes.some(r => r.id === detailRecipe.id)}
            onToggleSave={() => {
              const savedList = Array.isArray(savedRecipes) ? savedRecipes : [];
              const alreadySaved = savedList.some(r => r.id === detailRecipe.id);

              alreadySaved
                ? handleUnsaveClick(detailRecipe.id)
                : save(detailRecipe.id);
            }}
            onCustomize={user ? handleCustomizeClick : undefined}
            onViewFullPage={() => {
              // Navigate to the correct URL based on recipe type
              if (detailRecipe.type === 'variant') {
                navigate(`/variants/${detailRecipe.id}/view`);
              } else {
                navigate(`/recipes/${detailRecipe.id}/view`);
              }
            }}
          />
          </div>
        )}

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showUnsaveDialog}
          title="Remove Recipe"
          message="Are you sure you want to remove this recipe from your saved recipes?"
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={handleUnsaveConfirm}
          onCancel={handleUnsaveCancel}
          type="danger"
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showDeleteDialog}
          title="Delete Recipe"
          message="Are you sure you want to delete this recipe? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          type="danger"
        />
      </PageBody>
    </PageContainer>
  )
}

export default Recipes
